import {useActions, useGetters, useMutators, useState} from './_default';
import {
    MAP_DEFAULT_ZOOM_LEVEL,
    MAP_OBJECT_TYPE_GEOFENCE,
    MAP_OBJECT_TYPE_HISTORY_ARROW, MAP_OBJECT_TYPE_HISTORY_EVENT,
    MAP_OBJECT_TYPE_HISTORY_ROUTE,
    MAP_OBJECT_TYPE_HISTORY_STOP,
    MAP_OBJECT_TYPE_OBJECT,
    MAP_OBJECT_TYPE_POI,
    MAP_OBJECT_TYPE_ROUTE, MAP_OBJECT_TYPE_TAILS,
} from "@/js/constants.js";
import axios from "axios";
import {getFormLocalStorage, storeToLocalStorage} from "@/js/helpers.js";


const defaultMapSettings = {
    id: null,
    center: null,
    zoom: MAP_DEFAULT_ZOOM_LEVEL,
    controls: [],
    ready: false,
    units: {},
    keys: {},
    maps: [],
    showHtmlGeolocation: false,
};

export default {
    namespaced: true,
    state: {
        ...useState(),
        settings: defaultMapSettings,
        sharing: false,
        controls: [],
        showTools: true,
        historyControlsActive: false,
        homeViewEnabled: false,
        editHomeView: false,
        homeView: null,

        currentView: null,
        currentViewSettings: null,
        controlsList: {
            default: [
                {
                    icon: 'icon-expand',
                    name: 'full_screen',
                    alias: 'm_full_screen',
                    active: false,
                },
                {
                    icon: 'icon-cluster',
                    name: 'cluster',
                    alias: 'm_objects_cluster',
                    active: true,
                }, {
                    icon: 'icon-arrow-box',
                    name: 'fit_objects',
                    active: true,
                    static: true,
                }, {
                    icon: 'icon-object',
                    name: 'objects',
                    alias: 'm_objects',
                    loadMapObjects: true,
                    layer: MAP_OBJECT_TYPE_OBJECT,
                }, {
                    icon: 'icon-geofence',
                    name: 'geofences',
                    alias: 'm_geofences',
                    permission: 'geofence.view',
                    loadMapObjects: true,
                    layer: MAP_OBJECT_TYPE_GEOFENCE,
                }, {
                    icon: 'icon-routes',
                    name: 'routes',
                    alias: 'm_routes',
                    permission: 'route.view',
                    loadMapObjects: true,
                    layer: MAP_OBJECT_TYPE_ROUTE,
                }, {
                    icon: 'icon-pin',
                    name: 'poi',
                    alias: 'm_poi',
                    permission: 'poi.view',
                    loadMapObjects: true,
                    layer: MAP_OBJECT_TYPE_POI,
                }, {
                    icon: 'icon-group-6979',
                    name: 'show_names',
                    alias: 'm_show_names',
                }, {
                    icon: 'icon-tails',
                    name: 'show_tails',
                    alias: 'm_show_tails',
                    layer: MAP_OBJECT_TYPE_TAILS
                }, {
                    icon: 'icon-traffic',
                    name: 'show_traffic',
                    alias: 'm_traffic',
                }, {
                    icon: 'icon-dot-circle-o',
                    name: 'html_geolocation',
                    static: true,
                }
            ],

            history: [
                {
                    icon: 'icon-routes',
                    name: 'history_route',
                    title: 'front.route',
                    alias: 'history_control_route',
                    layer: MAP_OBJECT_TYPE_HISTORY_ROUTE,
                    active: true,
                }, {
                    icon: 'icon-object',
                    name: 'history_arrows',
                    title: 'front.arrows',
                    alias: 'history_control_arrows',
                    layer: MAP_OBJECT_TYPE_HISTORY_ARROW,
                    active: true,
                }, {
                    icon: 'icon-h-stop',
                    name: 'history_stops',
                    title: 'front.stops',
                    alias: 'history_control_stops',
                    layer: MAP_OBJECT_TYPE_HISTORY_STOP,
                    active: true,
                }, {
                    icon: 'icon-bell',
                    name: 'history_events',
                    title: 'front.events',
                    alias: 'history_control_events',
                    layer: MAP_OBJECT_TYPE_HISTORY_EVENT,
                    active: true,
                }
            ]
        },
    },
    getters: {
        ...useGetters(),
        controls: state => state.controls,
        fitObjectsControl: state => state.controls.find(control => control.name === 'fit_objects'),
        showTailControl: state => state.controls.find(control => control.name === 'show_tails'),
        showTrafficControl: state => state.controls.find(control => control.name === 'show_traffic'),
        clusterControl: state => state.controls.find(control => control.name === 'cluster'),
        objectsControl: state => state.controls.find(control => control.name === 'objects'),
        geofenceControl: state => state.controls.find(control => control.layer === MAP_OBJECT_TYPE_GEOFENCE),
        routeControl: state => state.controls.find(control => control.layer === MAP_OBJECT_TYPE_ROUTE),
        poiControl: state => {
            const list = state.historyControlsActive ? state.controlsList.default : state.controls;
            return list.find(control => control.layer === MAP_OBJECT_TYPE_POI);
        },
        historyEventControl: state => state.controls.find(c => c.layer === MAP_OBJECT_TYPE_HISTORY_EVENT),
        fullScreen: state => {
            return state.controlsList.default.find(c => c.name === 'full_screen')?.active;
        },
        fullScreenControl: state => state.controlsList.default.find(c => c.name === 'full_screen'),
        showTools: state => state.showTools,
        settings: state => state.settings,
        historyControlsActive: state => state.historyControlsActive,
        homeViewEnabled: state => state.homeViewEnabled,
        canEditHomeView: state => state.editHomeView,
        homeView: state => {
            if (!state.homeView) return;
            return {
                lat: state.homeView.center.lat,
                lng: state.homeView.center.lon,
                zoom: state.homeView.zoom
            };
        },
        currentViewEnabled: state => state.settings?.current_view_enabled,
        currentViewSettings: state => {
            if(state.currentViewSettings === null) {
                const settings = getFormLocalStorage('cvs');
                state.currentViewSettings = settings ? JSON.parse(settings) : false;
            }

            return state.currentViewSettings;
        },
        currentView: state => {
            if (state.currentView === null && state.settings?.current_view_enabled) {
                const currentView = getFormLocalStorage('cv');
                if (currentView) {
                    state.currentView = JSON.parse(currentView);
                }
            }
            return state.currentView;
        },
        showNamesControl: state => state.controlsList.default.find(control => control.name === 'show_names'),
    },
    mutations: {
        ...useMutators(),

        currentView(state, data) {
            if (!state.currentView) {
                state.currentView = state.currentViewSettings.strategy === 'last_view' ? { zoom: null, lat: null, lng: null} : { device_id: null };
            }

            if (state.currentViewSettings.strategy === 'last_view') {
                const { zoom, lat, lng } = data;
                if (typeof zoom !== 'undefined' && state.currentView.zoom !== zoom) {
                    state.currentView.zoom = zoom;
                    state.currentView.changed = true;
                }

                if (typeof lat !== 'undefined' && lat !== state.currentView.lat) {
                    state.currentView.lat = lat;
                    state.currentView.changed = true;
                }

                if (typeof lng !== 'undefined' && lng !== state.currentView.lng) {
                    state.currentView.lng = lng;
                    state.currentView.changed = true;
                }
                return;
            }

            if (state.currentViewSettings.strategy === 'last_select') {
                const { device_id } = data;
                if (typeof device_id !== 'undefined' && device_id !== state.currentView.device_id) {
                    state.currentView.device_id = device_id;
                    state.currentView.changed = true;
                }
            }
        },

        settings(state, settings) {
            state.settings = Object.assign(state.settings, settings);
        },

        tools(state, status) {
            state.showTools = status;
        },
        removeFromList(state, layerGroup) {
            if (!layerGroup._map) return;
            layerGroup._map.removeLayer(layerGroup);
        },
        setDefaultControls(state) {
            state.historyControlsActive = false;
            state.controls = state.controlsList.default.filter(control => {
                return control.name !== 'html_geolocation' || state.settings.showHtmlGeolocation;
            });
        },
        setHistoryControls(state) {
            state.historyControlsActive = true;
            state.controls = state.controlsList.history;
        },
        setSharingControls(state) {
            state.sharing = true;
            state.controls = [...state.controlsList.default].filter((c) => {
                return ['geofences', 'routes', 'poi', 'objects', 'html_geolocation'].indexOf(c.name) === -1;
            });
        },
        setControls(state, controls) {
            Object.keys(controls).forEach((key) => {
                let control = state.controlsList.default.find(c => c.name === key);
                if (!control) {
                    control = state.controlsList.history.find(c => c.name === key);
                }
                if (!control) return;
                control.active = controls[key] ? 1 : 0;
            });
        },

        disableFitBounds(state) {
            const control = state.controlsList.default.find(control => control.name === 'fit_objects');
            control.active = false;
        },
        enableFitBounds(state) {
            const control = state.controlsList.default.find(control => control.name === 'fit_objects');
            control.active = true;
        },

        enableClustering(state) {
            const control = state.controlsList.default.find(control => control.alias === 'm_objects_cluster');
            control.active = true;
        },

        enableObjectsControl(state) {
            const control = state.controlsList.default.find(control => control.alias === 'm_objects');
            control.active = true;
        },

        disableTrafficControl(state) {
            const control = state.controlsList.default.find(control => control.name === 'show_traffic');
            control.active = false;
            control.disabled = true;
        },

        enableTrafficControl(state) {
            const control = state.controlsList.default.find(control => control.name === 'show_traffic');
            control.disabled = false;
        },

        setUserMapId(state, id) {
            state.settings.id = id;
        },

        enableHomeView(state, status) {
            state.homeViewEnabled = status;
        },

        editHomeView(state, status) {
            state.editHomeView = status;
        },

        setHomeView(state, data) {
            state.homeView = data;
        }
    },
    actions: {
        ...useActions(),
        changeControlStatus({ state }, control) {
            return new Promise((resolve, reject) => {
                if (!control.alias || state.sharing) return resolve();
                axios.put('/map/update', {
                    param: control.alias,
                    value: control.active
                }).then(resolve).catch(reject);
            });
        },

        storeHomeView({ state }, { center, zoom }) {
            return new Promise((resolve, reject) => {
                axios.post('/home-view', {
                    lat: center.lat,
                    lon: center.lng,
                    zoom
                }).then((response) => {
                    state.homeView = response.data;
                    resolve();
                }).catch(reject);
            });

        },

        storeCurrentView({ state }) {
            if (!state.settings?.current_view_enabled || !state.currentView.changed) return;
            delete state.currentView.changed;
            console.trace('Current view store', state.currentView);
            storeToLocalStorage('cv', JSON.stringify(state.currentView));
        },

        storeCurrentViewSettings({ state }, settings) {
            state.currentViewSettings = settings;
            storeToLocalStorage('cvs', JSON.stringify(state.currentViewSettings));
        }
    },
}
